import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineFolderOpen,
  HiOutlineBuildingOffice,
  HiOutlineCurrencyDollar,
  HiOutlineChartBarSquare,
  HiOutlineSparkles,
} from 'react-icons/hi2';
import { listProjects, createProject } from '../../api/projects';
import { listOrganizations } from '../../api/organizations';
import { fetchDashboardStats } from '../../api/analytics';
import type { DashboardStats } from '../../api/analytics';
import { useAuthStore } from '../../stores/authStore';
import { hasMinRole } from '../../utils/rbac';
import { OnboardingWizard } from '../onboarding/OnboardingWizard';
import { OrgOverviewSection } from './OrgOverviewSection';
import { StatCard } from '../shared/StatCard';
import { ChartCard } from '../charts/ChartCard';
import { CostDonutChart } from '../charts/CostDonutChart';
import { buildCostDonutData } from '../charts/chartTheme';
import { fmt, fmtCHF, fmtM } from '../../utils/format';
import type { GrandProjet } from '../../types/estimation';
import { ActivityFeed } from '../activity/ActivityFeed';
import { InfoTip } from '../shared/InfoTip';
import { ModuleWidgets } from './ModuleWidgets';
import styles from './DashboardPage.module.css';

function TopProjectsList({ projects, hasCost }: { projects: { name: string; logements: number; emplois: number; cost: number }[]; hasCost: boolean }) {
  const { t } = useTranslation('common');
  if (projects.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No data</div>;

  const maxLog = Math.max(...projects.map((p) => p.logements), 1);
  const maxEmp = Math.max(...projects.map((p) => p.emplois), 1);
  const maxCost = Math.max(...projects.map((p) => p.cost), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 18, marginBottom: 10, fontSize: 11, color: '#94a3b8', letterSpacing: '0.02em' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: 'linear-gradient(135deg, #22d3ee, #5a8ab0)', display: 'inline-block' }} />
          {t('logements')}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: 'linear-gradient(135deg, #a78bfa, #a07db8)', display: 'inline-block' }} />
          {t('emplois')}
        </span>
        {hasCost && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: 'linear-gradient(135deg, #6366f1, #00a3d4)', display: 'inline-block' }} />
            M CHF
          </span>
        )}
      </div>

      {projects.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid rgba(255,255,255,0.04)', borderRadius: 6, transition: 'background 0.15s' }}>
          {/* Rank */}
          <span style={{ width: 22, height: 22, fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'center', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: 6 }}>
            {i + 1}
          </span>
          {/* Name */}
          <span style={{ width: 120, fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {p.name}
          </span>
          {/* Bars */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Logements bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${(p.logements / maxLog) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #22d3ee, #5a8ab0)', borderRadius: 4, minWidth: p.logements > 0 ? 2 : 0, transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#22d3ee', width: 50, textAlign: 'right', flexShrink: 0 }}>
                {p.logements > 0 ? p.logements.toLocaleString('fr-CH') : '\u2014'}
              </span>
            </div>
            {/* Emplois bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${(p.emplois / maxEmp) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #a78bfa, #a07db8)', borderRadius: 4, minWidth: p.emplois > 0 ? 2 : 0, transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#a78bfa', width: 50, textAlign: 'right', flexShrink: 0 }}>
                {p.emplois > 0 ? p.emplois.toLocaleString('fr-CH') : '\u2014'}
              </span>
            </div>
            {/* Cost bar (optional) */}
            {hasCost && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1, height: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${(p.cost / maxCost) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #00a3d4)', borderRadius: 4, minWidth: p.cost > 0 ? 2 : 0, transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', width: 50, textAlign: 'right', flexShrink: 0 }}>
                  {p.cost > 0 ? `${p.cost}M` : '\u2014'}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation(['common', 'dashboard']);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [projects, setProjects] = useState<GrandProjet[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [projectsData, orgsData, dashStats] = await Promise.all([
        listProjects({ mine: true }),
        listOrganizations('flat'),
        fetchDashboardStats().catch(() => null),
      ]);
      setProjects(projectsData);
      setStats(dashStats);

      // Show onboarding if admin and no orgs exist
      if (orgsData.length === 0 && user && hasMinRole(user.role, 'tenant_admin')) {
        setShowOnboarding(true);
      }
    } catch {
      // Handle error
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const project = await createProject({ name: newName });
      navigate(`/app/projects/${project.id}`);
    } catch {
      // Handle error
    }
  }

  if (showOnboarding) {
    return <OnboardingWizard onComplete={() => setShowOnboarding(false)} />;
  }

  const donutData = stats ? buildCostDonutData(stats.cost_breakdown) : [];

  const topProjects = stats?.projects_summary.slice(0, 5).map((p) => ({
    name: p.name.length > 18 ? p.name.slice(0, 18) + '...' : p.name,
    logements: p.logements,
    emplois: p.emplois,
    cost: Math.round(p.total_cost / 1_000_000 * 10) / 10, // in M CHF
  })) ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2>{t('dashboard:title')}</h2>
      </div>

      {/* Analytics stat cards */}
      {stats && stats.total_projects > 0 && (
        <>
          <div className={styles.statsRow}>
            <StatCard
              label={t('dashboard:totalProjects')}
              value={stats.total_projects}
              icon={<HiOutlineFolderOpen />}
              onClick={() => navigate('/app/stats/projects')}
              tooltip={t('dashboard:tooltips.totalProjects')}
            />
            <StatCard
              label={t('dashboard:housingCapacity')}
              value={fmt(stats.total_logements)}
              sub={t('dashboard:inhabitants', { count: Math.round(stats.total_logements * 2.3) })}
              icon={<HiOutlineBuildingOffice />}
              onClick={() => navigate('/app/stats/housing')}
              tooltip={t('dashboard:tooltips.housingCapacity')}
            />
            <StatCard
              label={t('dashboard:totalInvestment')}
              value={stats.total_cost > 0 ? fmtCHF(stats.total_cost) : '\u2014'}
              icon={<HiOutlineCurrencyDollar />}
              color="var(--color-primary)"
              onClick={() => navigate('/app/stats/investment')}
              tooltip={t('dashboard:tooltips.totalInvestment')}
            />
            <StatCard
              label={t('dashboard:avgDensity')}
              value={stats.avg_density > 0 ? `${stats.avg_density}` : '\u2014'}
              sub={t('common:logementsPerHa')}
              icon={<HiOutlineChartBarSquare />}
              onClick={() => navigate('/app/stats/density')}
              tooltip={t('dashboard:tooltips.avgDensity')}
            />
          </div>

          {/* Organizational Hierarchy Overview */}
          <OrgOverviewSection />

          {/* CIVITAS Module KPI Widgets */}
          <ModuleWidgets />

          <div className={styles.chartsRow}>
            <ChartCard title={<>{t('dashboard:costBreakdown')}<InfoTip text={t('dashboard:tooltips.costBreakdown')} /></>} subtitle={t('dashboard:byCategory')}>
              <CostDonutChart
                data={donutData}
                centerValue={stats.total_cost > 0 ? fmtM(stats.total_cost) : undefined}
                centerLabel={stats.total_cost > 0 ? t('common:total') : undefined}
              />
            </ChartCard>
            <ChartCard title={<>{t('dashboard:topProjects')}<InfoTip text={t('dashboard:tooltips.topProjects')} /></>} subtitle={t('dashboard:byCapacity')} height="auto">
              <TopProjectsList projects={topProjects} hasCost={topProjects.some((p) => p.cost > 0)} />
            </ChartCard>
          </div>

          {/* Recent Activity */}
          <div className={styles.activitySection}>
            <h3 className={styles.sectionTitle}>{t('dashboard:recentActivity')}</h3>
            <div className={styles.activityPanel}>
              <ActivityFeed limit={8} compact />
            </div>
          </div>
        </>
      )}

      {/* Projects Section */}
      <div className={styles.sectionDivider}>
        <h3 className={styles.sectionTitle}>{t('dashboard:myProjects')}</h3>
        <button className={styles.createBtn} onClick={() => setShowCreate(true)}>
          + {t('common:newProject')}
        </button>
      </div>

      {showCreate && (
        <form className={styles.createForm} onSubmit={handleCreate}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t('common:projectName')}
            autoFocus
          />
          <button type="submit">{t('common:create', 'Create')}</button>
          <button type="button" onClick={() => setShowCreate(false)}>
            {t('common:cancel', 'Cancel')}
          </button>
        </form>
      )}

      {loading ? (
        <p className={styles.loading}>{t('common:loading', 'Loading...')}</p>
      ) : projects.length === 0 ? (
        <div className={styles.onboardCard}>
          <div className={styles.onboardIcon}><HiOutlineSparkles /></div>
          <h3 className={styles.onboardTitle}>
            {t('dashboard:welcomeToQenq', 'Bienvenue sur QENQ')}
          </h3>
          <p className={styles.onboardIntro}>
            {t('dashboard:emptyProjectsIntro', "QENQ estime les coûts d'urbanisation pour vos périmètres de projet.")}
          </p>
          <div className={styles.onboardActions}>
            <button className={styles.onboardPrimary} onClick={() => setShowCreate(true)}>
              + {t('dashboard:createFirstProjectCta', 'Créer mon premier projet')}
            </button>
            <button
              className={styles.onboardSecondary}
              onClick={() => navigate('/app/reference-projects')}
            >
              {t('dashboard:browseReferenceProjects', 'Explorer les projets de référence')}
            </button>
          </div>
          <p className={styles.onboardFootnote}>
            {t('dashboard:orLearnBy', '')}
          </p>
        </div>
      ) : (
        <div className={styles.grid}>
          {projects.map((project) => (
            <div
              key={project.id}
              className={styles.card}
              onClick={() => navigate(`/app/projects/${project.id}`)}
            >
              <div className={styles.cardInner}>
                <h3 className={styles.cardTitle}>{project.name}</h3>
                {project.description && (
                  <p className={styles.cardDesc}>{project.description}</p>
                )}
                <div className={styles.cardMeta}>
                  <span>N{project.niveau}</span>
                  <span>{project.ha_total ?? 0} ha</span>
                  <span className={styles.status}>{project.status}</span>
                </div>
                <time className={styles.cardDate}>
                  {new Date(project.updated_at).toLocaleDateString('fr-CH')}
                </time>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
